import React, { useCallback, useState } from 'react';
import { TeamGraph } from './components/TeamGraph';
import { FilterSidebar } from './components/FilterSidebar';
import { AnalysisPanel } from './components/AnalysisPanel';
import { TopMetrics } from './components/TopMetrics';
import { founderData, calculateCompatibility } from './utils/mockData';
import { Founder } from './utils/types';
import { PasswordProtectedWrapper } from './components/PasswordProtectedWrapper';
interface AppProps {
  onSignOut?: () => void;
}
export function App({
  onSignOut
}: AppProps) {
  const [selectedFounder, setSelectedFounder] = useState<Founder | null>(null);
  const [comparedFounder, setComparedFounder] = useState<Founder | null>(null);
  const [isRealData, setIsRealData] = useState(false);
  const [filters, setFilters] = useState({
    archetypes: {
      CEO: true,
      CTO: true
    },
    skills: {
      'AI/ML': true,
      Enterprise: true,
      Consumer: true,
      Fintech: true,
      Health: true,
      Marketplace: true,
      SaaS: true,
      Security: true,
      'Venture Capital': true,
      'Crypto/Web3': true
    },
    previousExit: false,
    repeatFounder: false
  });
  // Real data for Divanny Lamas
  const divannyLamas: Founder = {
    id: '13',
    name: 'Divanny Lamas',
    archetype: 'CEO',
    skills: ['Enterprise', 'SaaS', 'Marketplace'],
    previousCompanies: [{
      name: 'Transposit',
      role: 'CEO',
      years: [2019, 2024]
    }, {
      name: 'Sutter Hill Ventures',
      role: 'Managing Director',
      years: [2019, 2022]
    }, {
      name: 'Splunk',
      role: 'Head of New Product Introduction',
      years: [2018, 2019]
    }, {
      name: 'Splunk',
      role: 'Sr. Director, Customer Success',
      years: [2017, 2018]
    }, {
      name: 'Splunk',
      role: 'Head of Product - Platform',
      years: [2013, 2015]
    }, {
      name: 'Google',
      role: 'Ops @ Google Enterprise Apps',
      years: [2010, 2011]
    }, {
      name: 'Evernote',
      role: 'Support Manager',
      years: [2008, 2009]
    }],
    education: [{
      institution: 'Harvard University',
      degree: 'A.B., Government and Computer Science',
      year: 2008
    }],
    yearsExperience: 16,
    exitHistory: [{
      company: 'Transposit',
      year: 2024,
      value: 'IP Sale to Harness'
    }],
    velocityScore: 88,
    potentialScore: 92,
    photoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2zuvN1YUFG4hPLQf7Pjap4/pasted-image.jpg',
    linkedinUrl: 'https://www.linkedin.com/in/divanny/'
  };
  // Real data for Kevin Chan
  const kevinChan: Founder = {
    id: '9',
    name: 'Kevin Chan',
    archetype: 'CTO',
    skills: ['AI/ML', 'Enterprise', 'Fintech'],
    previousCompanies: [{
      name: 'Ramp',
      role: 'Staff Software Engineer, Procurement',
      years: [2023, 2025]
    }, {
      name: 'Venue',
      role: 'Co-Founder, CPO',
      years: [2022, 2023]
    }, {
      name: 'Curaga AG',
      role: 'Engineering Lead',
      years: [2022, 2022]
    }, {
      name: 'Meta',
      role: 'Senior Software Engineer',
      years: [2022, 2022]
    }, {
      name: 'Meta',
      role: 'Software Engineer',
      years: [2020, 2021]
    }, {
      name: 'Italic',
      role: 'Software Engineer Consultant',
      years: [2020, 2020]
    }],
    education: [{
      institution: 'Cornell University',
      degree: 'Bachelor of Science - BS, Computer Science',
      year: 2020
    }, {
      institution: 'Cornell University',
      degree: 'Master of Engineering - MEng, Computer Science',
      year: 2020
    }],
    yearsExperience: 5,
    exitHistory: [{
      company: 'Venue',
      year: 2023,
      value: 'Acquired by Ramp'
    }],
    velocityScore: 92,
    potentialScore: 89,
    photoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/98bxKyW1TMkZHWRisSbNQ1/kevinchan159.jpg',
    linkedinUrl: 'https://www.linkedin.com/in/kevinchan159/'
  };
  // Fictional data for Kathryn Janeway (modified from Divanny)
  const kathrynJaneway: Founder = {
    ...divannyLamas,
    name: 'Kathryn Janeway',
    previousCompanies: divannyLamas.previousCompanies.map(company => ({
      ...company,
      name: company.name === 'Transposit' ? 'Breakthrough Startup' : company.name === 'Sutter Hill Ventures' ? 'VC Firm' : company.name === 'Splunk' ? 'Unicorn Startup' : company.name
    })),
    exitHistory: [{
      company: 'Breakthrough Startup',
      year: 2024,
      value: 'IP Sale to Public Company'
    }],
    photoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/52eRQFiGewCM8aUatc2rjS/pasted-image.webp'
  };
  // Fictional data for Seven of Nine (modified from Kevin)
  const sevenOfNine: Founder = {
    ...kevinChan,
    name: 'Seven of Nine',
    previousCompanies: kevinChan.previousCompanies.map(company => ({
      ...company,
      name: company.name === 'Ramp' ? 'Unicorn Startup' : company.name === 'Venue' ? 'Seed Startup' : company.name === 'Curaga AG' ? 'Series C startup' : company.name
    })),
    exitHistory: [{
      company: 'Seed Startup',
      year: 2023,
      value: 'Acquired by Unicorn Startup'
    }],
    photoUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mm9wQtDsj4MmLpFRjYSpuZ/pasted-image.webp'
  };
  // Function to toggle between real and dummy data
  const toggleData = useCallback(() => {
    setIsRealData(prev => !prev);
    // Reset selected founders when toggling to avoid any state issues
    setSelectedFounder(null);
    setComparedFounder(null);
  }, []);
  const handleFounderSelect = (founder: Founder, isShiftKey: boolean) => {
    if (isShiftKey && selectedFounder) {
      setComparedFounder(founder);
    } else {
      setSelectedFounder(founder);
      setComparedFounder(null);
    }
  };
  // Helper function to check if someone is a repeat founder
  const isRepeatFounder = (founder: Founder): boolean => {
    return founder.previousCompanies.some(company => company.role.toLowerCase().includes('founder') || company.role.toLowerCase().includes('co-founder'));
  };
  // Get the modified founder data with real or dummy data for specific founders
  const getModifiedFounderData = () => {
    return founderData.map(founder => {
      if (founder.id === '13') {
        return isRealData ? divannyLamas : kathrynJaneway;
      } else if (founder.id === '9') {
        return isRealData ? kevinChan : sevenOfNine;
      }
      return founder;
    });
  };
  const filteredFounders = getModifiedFounderData().filter(founder => {
    // Filter by archetype
    if (!filters.archetypes[founder.archetype as keyof typeof filters.archetypes]) return false;
    // Filter by exit history
    if (filters.previousExit && founder.exitHistory.length === 0) return false;
    // Filter by repeat founder
    if (filters.repeatFounder && !isRepeatFounder(founder)) return false;
    // Check if any of the founder's skills are in the filter
    const hasMatchingSkill = founder.skills.some(skill => filters.skills[skill as keyof typeof filters.skills]);
    // Include the founder if they have at least one matching skill or if no specific skills are selected
    return hasMatchingSkill || Object.values(filters.skills).every(value => !value);
  });
  return <PasswordProtectedWrapper data-id="element-0">
      <div className="flex flex-col w-full h-screen bg-[#0a0a0a] text-white font-mono" data-id="element-1">
        <TopMetrics onSignOut={onSignOut} data-id="element-2" />
        <div className="flex flex-1 overflow-hidden" data-id="element-3">
          <FilterSidebar filters={filters} setFilters={setFilters} toggleData={toggleData} isRealData={isRealData} data-id="element-4" />
          <div className="flex-1 relative overflow-hidden" data-id="element-5">
            <TeamGraph founders={filteredFounders} selectedFounder={selectedFounder} comparedFounder={comparedFounder} onFounderSelect={handleFounderSelect} data-id="element-6" />
          </div>
          <AnalysisPanel selectedFounder={selectedFounder} comparedFounder={comparedFounder} compatibility={selectedFounder && comparedFounder ? calculateCompatibility(selectedFounder, comparedFounder) : null} data-id="element-7" />
        </div>
      </div>
    </PasswordProtectedWrapper>;
}